import { useState } from 'react';

import { Button } from '../../components/ui/Button';
import { Field, TextField } from '../../components/ui/Field';
import { Select } from '../../components/ui/Select';
import { isGender, type Gender } from '../../lib/gender';
import { useLocale } from '../../lib/useLocale';
import { CountrySelect } from './CountrySelect';

export interface PersonalInfoValues {
  nickname: string;
  birthday: string;
  gender: Gender;
  countryCode: string;
}

export function PersonalInfoForm({
  initial,
  submitLabel,
  busy,
  error,
  onSubmit,
}: {
  initial?: Partial<PersonalInfoValues>;
  submitLabel: string;
  busy?: boolean;
  error?: string | null;
  onSubmit: (values: PersonalInfoValues) => void | Promise<void>;
}) {
  const { t } = useLocale();
  const [nickname, setNickname] = useState(initial?.nickname ?? '');
  const [birthday, setBirthday] = useState(initial?.birthday ?? '');
  const [gender, setGender] = useState<Gender | ''>(initial?.gender ?? '');
  const [countryCode, setCountryCode] = useState(initial?.countryCode ?? '');
  const [localError, setLocalError] = useState<string | null>(null);

  const canSubmit = nickname.trim() !== '' && isGender(gender) && countryCode.trim() !== '';

  return (
    <form
      className="space-y-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        setLocalError(null);
        if (nickname.trim() === '') {
          setLocalError(t('auth.nicknameRequired'));
          return;
        }
        if (!isGender(gender)) {
          setLocalError(t('auth.genderRequired'));
          return;
        }
        if (countryCode.trim() === '') {
          setLocalError(t('auth.nationalityRequired'));
          return;
        }
        void onSubmit({
          nickname: nickname.trim(),
          birthday: birthday.trim(),
          gender,
          countryCode: countryCode.trim(),
        } satisfies PersonalInfoValues);
      }}
    >
      <TextField
        label={t('auth.nickname')}
        hint={t('auth.nicknameHint')}
        autoComplete="nickname"
        value={nickname}
        onChange={(event) => setNickname(event.target.value)}
        maxLength={24}
        required
      />
      <TextField
        label={t('auth.birthday')}
        hint={t('common.optional')}
        type="date"
        value={birthday}
        onChange={(event) => setBirthday(event.target.value)}
      />
      <Field label={t('auth.gender')} required>
        <Select
          value={gender}
          onValueChange={(value) => setGender(isGender(value) ? value : '')}
          placeholder={t('auth.genderPlaceholder')}
          options={[
            { value: 'male', label: t('auth.genderMale') },
            { value: 'female', label: t('auth.genderFemale') },
            { value: 'other', label: t('auth.genderOther') },
            { value: 'prefer_not_to_say', label: t('auth.genderUnspecified') },
          ]}
          aria-label={t('auth.gender')}
        />
      </Field>
      <Field label={t('auth.country')} required>
        <CountrySelect value={countryCode} onChange={setCountryCode} />
      </Field>
      {localError || error ? (
        <p role="alert" className="border-danger/30 bg-danger-soft text-danger rounded-md border px-3 py-2 text-xs">
          {localError || error}
        </p>
      ) : null}
      <Button type="submit" size="lg" className="w-full" isLoading={busy} disabled={busy || !canSubmit}>
        {submitLabel}
      </Button>
    </form>
  );
}
